/**
 * Which nested endpoint serves each mountable record's activity feed (E9 ruling G2).
 *
 * ## Why the endpoint is nested on the parent module, and must not be "simplified"
 *
 * The panel's authorization **rides the parent document's view rule** — that is why reading a picklist's own
 * history needs no `activity.*` permission at all. A single generic `GET /api/activity/entity/{type}/{id}`
 * could not ride that rule without re-implementing every module's access check inside the activity feature,
 * which is the exact coupling this shape avoids. So the endpoint count grows with the mount count,
 * deliberately, and each mounted module keeps a one-line action delegating to the shared service.
 *
 * ## Why a `Record` over a union instead of a switch
 *
 * This replaces a runtime `throw` on an unknown entity type. That throw was fail-closed and well-intentioned,
 * but the panel is now mounted on detail views, so an unregistered type would crash the page for a user. The
 * `Record<ActivityEntityType, …>` gives a STRONGER guarantee EARLIER and for free: adding a member to the
 * union without adding a route here is a `tsc` error at build time. Nothing can reach production unmapped.
 */

/**
 * Records that can mount the activity panel today.
 *
 * ⚠️ **v1 is Picklist and Receiving only, and that is a decision rather than a stopping point.** They are the
 * only two surfaces built on `DetailPageLayout`, whose rail slot is already the docked geometry the design
 * specifies — and the only two entity types `ActivityLogs` meaningfully contains. Mounting an empty panel on
 * a module whose events do not exist yet would ship the design's own anti-pattern ("an empty filter is worse
 * than no filter") at panel scale. Sales Orders and Quotations are *workspace* pages — a master list beside a
 * detail panel — which is a design question about geometry, not a build decision.
 *
 * Adding one is: a controller action, a member here, and a route below. The compiler enforces the third.
 */
export type ActivityEntityType = 'Picklist' | 'Receiving';

/**
 * TOTAL over `ActivityEntityType`. Remove a line and the build fails — try it before trusting this comment.
 */
export const ENTITY_ACTIVITY_ROUTES: Record<
  ActivityEntityType,
  (id: string | number) => string
> = {
  Picklist: (id) => `/picklist/${id}/activity`,
  Receiving: (id) => `/receiving/${id}/activity`,
};

/** Type guard for the boundary where a plain string arrives (route params, older props). */
export function isActivityEntityType(value: string): value is ActivityEntityType {
  return value in ENTITY_ACTIVITY_ROUTES;
}
